import motion from '@lib/motion';
import { listen } from '@theme/events';
import { SKY_THEME_EVENTS } from '@theme/config';
import { getFocusableElements, getNextFocusableAfter, motionReduced, restoreFocusTo } from '@theme/theme-global';

if (!customElements.get('sticky-add-to-cart-success')) {
  customElements.define(
    'sticky-add-to-cart-success',
    class StickyAddToCartSuccess extends HTMLElement {
      connectedCallback() {
        this.successSlot = this.querySelector('[data-sticky-success-slot]');
        this.successSectionId = this.dataset.successSection || 'sticky-add-to-cart-success';
        this.isAnimating = false;
        this.isActive = false;
        this.hideTimeoutId = null;
        this.hideDelayMs = this.getHideDelayMs();
        this.returnFocusEl = null;
        this._openedCartDrawer = false;
        this._onKeydown = this.onKeydown.bind(this);
        this._onViewCartClick = this.onViewCartClick.bind(this);

        if (!this.successSlot) return;

        this.cartUpdateUnsubscriber = listen(SKY_THEME_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        this.addEventListener('click', this._onViewCartClick);
        this.addEventListener('keydown', this._onKeydown);
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
        this.clearHideTimer();
        this.removeEventListener('click', this._onViewCartClick);
        this.removeEventListener('keydown', this._onKeydown);
      }

      getHideDelayMs() {
        const seconds = Number(this.dataset.hideDelay);
        if (!Number.isFinite(seconds) || seconds <= 0) return 0;
        return Math.round(seconds * 1000);
      }

      clearHideTimer() {
        if (this.hideTimeoutId == null) return;
        clearTimeout(this.hideTimeoutId);
        this.hideTimeoutId = null;
      }

      scheduleHide() {
        this.clearHideTimer();
        if (!this.hideDelayMs) return;

        this.hideTimeoutId = setTimeout(() => {
          this.hideTimeoutId = null;
          this.hide(true);
        }, this.hideDelayMs);
      }

      onCartUpdate(event) {
        if (event?.source !== 'buy-form') return;
        if (event?.cartData?.status) return;

        const sectionHtml = event?.cartData?.sections?.[this.successSectionId];
        if (!sectionHtml) return;

        if (event.returnFocus && document.contains(event.returnFocus)) {
          this.returnFocusEl = event.returnFocus;
        }

        this._openedCartDrawer = false;
        this.renderSuccess(sectionHtml, event.cartData);
      }

      async renderSuccess(sectionHtml, cartData) {
        if (!this.successSlot) return;

        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const successContent =
          parsed.querySelector('[data-sticky-add-to-cart-success="true"]') ||
          parsed.querySelector('.shopify-section')?.firstElementChild;

        if (!successContent) return;

        this.successSlot.innerHTML = '';
        this.successSlot.appendChild(successContent.cloneNode(true));
        this.applyProductMessage(cartData);

        if (motionReduced()) {
          this.showInstant();
          return;
        }

        await this.animateIn();
      }

      applyProductMessage(cartData) {
        const messageEl = this.successSlot.querySelector('[data-sticky-success-message-text]');
        if (!messageEl) return;

        const productLabel = this.getAddedProductLabel(cartData);
        if (!productLabel) return;

        const messageTemplate =
          window.variantStrings?.stickyAddedToCart || '“[product]” has been added to your cart';
        messageEl.textContent = messageTemplate.replace('[product]', productLabel);
      }

      getAddedProductLabel(cartData) {
        const item = this.resolveAddedItem(cartData);
        if (!item) return '';

        const productTitle = item.product_title || item.title || '';
        const variantTitle = item.variant_title;

        if (variantTitle && variantTitle !== 'Default Title') {
          return `${productTitle} - ${variantTitle}`;
        }

        return productTitle;
      }

      resolveAddedItem(cartData) {
        if (!cartData) return null;

        if (Array.isArray(cartData.items) && cartData.items.length) {
          const hasGift = cartData.items.some((item) => {
            const properties = item.properties || {};
            return properties._gift === 'true' || properties['_gift'] === 'true';
          });

          if (hasGift) {
            return (
              cartData.items.find((item) => {
                const properties = item.properties || {};
                return properties._gift !== 'true' && properties['_gift'] !== 'true';
              }) || cartData.items[0]
            );
          }

          return cartData.items[cartData.items.length - 1];
        }

        return cartData;
      }

      getSuccessFocusTarget() {
        return (
          this.successSlot?.querySelector('[data-sticky-view-cart]') ||
          this.successSlot?.querySelector('a, button') ||
          null
        );
      }

      focusSuccessPanel() {
        const target = this.getSuccessFocusTarget();
        if (!target) return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => restoreFocusTo(target));
        });
      }

      restoreReturnFocus() {
        const el = this.returnFocusEl;
        this.returnFocusEl = null;
        restoreFocusTo(el);
      }

      /**
       * Focus target after keyboard dismiss from the sticky bar:
       * next focusable after the ATC/QV source, never the page top by accident.
       */
      resolveTabDismissFocus() {
        const anchor = this.returnFocusEl;
        const next = getNextFocusableAfter(anchor, { exclude: this });
        if (next && document.contains(next)) return next;
        if (anchor && document.contains(anchor)) return anchor;
        return null;
      }

      /**
       * Tab or Shift+Tab from the sticky edge closes the notification (drawer + page)
       * and lands on the same post-QV focus target — not the document start.
       */
      onKeydown(event) {
        if (event.key !== 'Tab') return;
        if (!this.isActive && !this.classList.contains('is-visible')) return;

        const active = document.activeElement;
        if (!active || !this.contains(active)) return;

        const stickyFocusables = getFocusableElements(this);
        const index = stickyFocusables.indexOf(active);
        if (index < 0) return;

        // Allow Tab / Shift+Tab to move between controls inside the bar when possible
        if (!event.shiftKey && index < stickyFocusables.length - 1) return;
        if (event.shiftKey && index > 0) return;

        event.preventDefault();
        this.dismissForTab();
      }

      async dismissForTab() {
        const focusTarget = this.resolveTabDismissFocus();
        // Move focus before unmount so Chrome cannot park on <body> / page top
        if (focusTarget && document.contains(focusTarget)) {
          restoreFocusTo(focusTarget);
        }
        await this.hide(true, { skipReturnFocus: true });
      }

      showInstant() {
        this.isActive = true;
        this.classList.add('is-visible');
        document.dispatchEvent(new CustomEvent('sticky-atc-success:show'));
        this.focusSuccessPanel();
        this.scheduleHide();
      }

      async animateIn() {
        if (this.isAnimating) return;
        this.isAnimating = true;

        const wasVisible = this.classList.contains('is-visible');
        const panel = this.successSlot.querySelector('[data-sticky-add-to-cart-success="true"]') || this.successSlot;
        const duration = 1.1;
        const easing = [0.4, 0, 0.2, 1];

        this.isActive = true;
        document.dispatchEvent(new CustomEvent('sticky-atc-success:show'));
        this.focusSuccessPanel();
        this.scheduleHide();

        if (!wasVisible) {
          panel.style.opacity = '1';
          this.classList.add('is-visible');
          this.isAnimating = false;
          return;
        }

        panel.style.opacity = '0';
        this.classList.add('is-visible');
        await motion.animate(panel, { opacity: [0, 1] }, { duration, easing }).finished;
        panel.style.opacity = '';
        this.isAnimating = false;
      }

      async hide(permanent = false, { skipReturnFocus = false } = {}) {
        if (!this.isActive && !this.classList.contains('is-visible')) return;

        this.clearHideTimer();
        this.isActive = false;
        this.classList.remove('is-visible');

        const active = document.activeElement;
        const focusWasInSticky = this.contains(active);
        // Don't steal focus if the user already tabbed to another page control
        const focusMovedElsewhere =
          active &&
          active !== document.body &&
          active !== document.documentElement &&
          !focusWasInSticky &&
          active !== this.returnFocusEl;

        if (!motionReduced()) {
          await this.waitForHideTransition();
        }

        if (permanent && this.successSlot) {
          this.successSlot.innerHTML = '';
        }

        document.dispatchEvent(new CustomEvent('sticky-atc-success:hide'));

        // QV/PDP ATC: return to source (card quick-view button / ATC) when sticky dismisses
        if (!skipReturnFocus && !this._openedCartDrawer && !focusMovedElsewhere) {
          this.restoreReturnFocus();
        } else {
          this.returnFocusEl = null;
        }
      }

      waitForHideTransition() {
        return new Promise((resolve) => {
          let resolved = false;
          const finish = () => {
            if (resolved) return;
            resolved = true;
            this.removeEventListener('transitionend', onEnd);
            resolve();
          };

          const onEnd = (event) => {
            if (event.target !== this) return;
            if (event.propertyName !== 'transform') return;
            finish();
          };

          this.addEventListener('transitionend', onEnd);
          setTimeout(finish, 1200);
        });
      }

      onViewCartClick(event) {
        const viewCartLink = event.target.closest('[data-sticky-view-cart]');
        if (!viewCartLink || !this.contains(viewCartLink)) return;
        if (this.dataset.cartType !== 'drawer') return;

        const cartDrawer = document.querySelector('bag-drawer');
        if (!cartDrawer) return;

        event.preventDefault();

        // Case 7: drawer should return to original ATC, not this View cart link
        const returnTo = this.returnFocusEl;
        this._openedCartDrawer = true;
        this.hide(true, { skipReturnFocus: true });

        if (returnTo && document.contains(returnTo)) {
          cartDrawer.rememberFocus(returnTo);
        }
        cartDrawer.open();
      }
    },
  );
}
